import { ComponentRef, FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  ErrorSummary,
  Field,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { PlusIcon } from '@navikt/aksel-icons';
import { CreateCustomSurveyTemplateRequest, TemplateScope } from '../../../api/customSurveyTemplates';
import { useGetAllTeamsForHrQuery } from '../../../hooks/queries/useGetAllTeamsForHrQuery';
import { useGetCustomSurveyTemplatesQuery } from '../../../hooks/queries/useGetCustomSurveyTemplatesQuery';
import { CategorySelect } from './CategorySelect';
import { QuestionEditor } from './QuestionEditor';
import {
  copyQuestion,
  Draft,
  DraftError,
  DraftQuestion,
  knownCategories,
  move,
  newQuestion,
  toRequest,
  validateDraft,
} from '../templateDraft';
import classes from '../CreateCustomSurveyTemplatePage.module.css';

type Crumb = { label: string; to: string };

type TemplateFormProps = {
  mode: 'create' | 'edit';
  initialDraft: Draft;
  /** Brødsmulene, der den siste er siden man står på */
  crumbs: Crumb[];
  eyebrow: string;
  /** Tittel når navnet er tomt */
  fallbackTitle: string;
  /** Vises ved siden av tittelen, for eksempel versjonen */
  titleExtra?: ReactNode;
  /** Vises under tittelen: en forklaring eller et varsel */
  intro: ReactNode;
  submitLabel: string;
  isPending: boolean;
  saveFailed: boolean;
  cancelTo: string;
  onSubmit: (request: CreateCustomSurveyTemplateRequest) => void;
};

/** Skjemaet for å opprette eller redigere en mal: om malen, spørsmålene og en fast knapperad */
export const TemplateForm = ({
  mode,
  initialDraft,
  crumbs,
  eyebrow,
  fallbackTitle,
  titleExtra,
  intro,
  submitLabel,
  isPending,
  saveFailed,
  cancelTo,
  onSubmit,
}: TemplateFormProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [openKey, setOpenKey] = useState<number | null>(() => draft.questions[0]?.key ?? null);
  const [tried, setTried] = useState(false);
  const [added, setAdded] = useState<string[]>([]);
  const [announcement, setAnnouncement] = useState('');
  const [focusTarget, setFocusTarget] = useState<string | null>(null);
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);
  const [summaryTick, setSummaryTick] = useState(0);

  const { data: templates = [] } = useGetCustomSurveyTemplatesQuery();
  const { data: teams = [] } = useGetAllTeamsForHrQuery();
  // Kategoriene malen har fra før er med, også når de ikke finnes i andre maler
  const ownCategories = useMemo(
    () => [initialDraft.category, ...initialDraft.questions.map((question) => question.category)].filter(Boolean),
    [initialDraft],
  );
  const categories = useMemo(
    () =>
      [...new Set([...knownCategories(templates), ...ownCategories, ...added])].sort((a, b) => a.localeCompare(b, 'nb')),
    [templates, ownCategories, added],
  );

  const errors = tried ? validateDraft(draft) : [];
  const templateError = (fieldId: string) => errors.find((error) => error.fieldId === fieldId);
  const nameError = templateError('template-name');
  const teamError = templateError('template-team');

  useEffect(() => {
    if (summaryTick > 0) summaryRef.current?.focus();
  }, [summaryTick]);

  useEffect(() => {
    if (!focusTarget) return;
    document.getElementById(focusTarget)?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const update = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));
  const setQuestions = (update: (questions: DraftQuestion[]) => DraftQuestion[]) =>
    setDraft((current) => ({ ...current, questions: update(current.questions) }));

  const addCategory = (category: string) => setAdded((current) => (current.includes(category) ? current : [...current, category]));

  const addQuestion = () => {
    const question = newQuestion();
    setQuestions((questions) => [...questions, question]);
    setOpenKey(question.key);
    setFocusTarget(`question-${question.key}-text`);
  };

  const moveQuestion = (index: number, delta: number) => {
    setQuestions((questions) => move(questions, index, index + delta));
    setAnnouncement(
      t('customSurveys.create.moved', { from: index + 1, to: index + delta + 1, total: draft.questions.length }),
    );
  };

  const goToError = (error: DraftError) => {
    if (error.questionKey !== undefined) setOpenKey(error.questionKey);
    setFocusTarget(error.fieldId);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (validateDraft(draft).length > 0) {
      setSummaryTick((tick) => tick + 1);
      return;
    }
    onSubmit(toRequest(draft));
  };

  const count = draft.questions.length;
  const title = draft.name.trim() || fallbackTitle;

  return (
    <form className={classes.page} onSubmit={submit} noValidate>
      <nav aria-label={t('breadcrumbs.ariaLabel')} className="ds-breadcrumbs">
        <ol>
          {crumbs.map((crumb, index) => (
            <li key={crumb.to}>
              <Link to={crumb.to} className="ds-link" aria-current={index === crumbs.length - 1 ? 'page' : undefined}>
                {crumb.label}
              </Link>
            </li>
          ))}
        </ol>
      </nav>

      <div className={classes.intro}>
        <span className={classes.eyebrow}>{eyebrow}</span>
        <div className={classes.titleRow}>
          <Heading level={1} data-size="xl">
            {title}
          </Heading>
          {titleExtra}
        </div>
      </div>
      {intro}

      {errors.length > 0 && (
        <ErrorSummary ref={summaryRef}>
          <ErrorSummary.Heading>{t('customSurveys.create.errorSummary')}</ErrorSummary.Heading>
          <ErrorSummary.List>
            {errors.map((error) => (
              <ErrorSummary.Item key={error.fieldId + error.message}>
                <ErrorSummary.Link
                  href={`#${error.fieldId}`}
                  onClick={(event) => {
                    event.preventDefault();
                    goToError(error);
                  }}
                >
                  {t(error.message, error.params)}
                </ErrorSummary.Link>
              </ErrorSummary.Item>
            ))}
          </ErrorSummary.List>
        </ErrorSummary>
      )}

      {saveFailed && (
        <Alert data-color="danger" role="alert">
          <Paragraph>{t('customSurveys.create.saveFailed')}</Paragraph>
        </Alert>
      )}

      <section aria-labelledby="template-about" className={classes.card}>
        <Heading level={2} data-size="xs" id="template-about">
          {t('customSurveys.create.about')}
        </Heading>
        <Field>
          <Label htmlFor="template-name">{t('customSurveys.create.name')}</Label>
          <Input
            id="template-name"
            value={draft.name}
            autoComplete="off"
            aria-invalid={!!nameError}
            aria-describedby={nameError ? 'template-name-error' : undefined}
            onChange={(event) => update({ name: event.target.value })}
          />
          {nameError && <ValidationMessage id="template-name-error">{t(nameError.message)}</ValidationMessage>}
        </Field>
        <Field>
          <Label htmlFor="template-description" className={classes.optionalLabel}>
            {t('customSurveys.create.descriptionLabel')}
            <Tag data-size="sm" data-color="neutral">
              {t('customSurveys.create.optional')}
            </Tag>
          </Label>
          <Paragraph data-size="sm" id="template-description-hint" className={classes.subtle}>
            {t('customSurveys.create.descriptionHint')}
          </Paragraph>
          <Textarea
            id="template-description"
            rows={3}
            aria-describedby="template-description-hint"
            value={draft.description}
            onChange={(event) => update({ description: event.target.value })}
          />
        </Field>
        <CategorySelect
          id="template-category"
          value={draft.category}
          categories={categories}
          onChange={(category) => update({ category })}
          onAdd={addCategory}
        />
        {mode === 'create' && (
        <div className={classes.scopeRow}>
          <Field className={classes.selectField}>
            <Label htmlFor="template-scope">{t('customSurveys.create.scope')}</Label>
            <Paragraph data-size="sm" id="template-scope-hint" className={classes.subtle}>
              {t('customSurveys.create.scopeHint')}
            </Paragraph>
            <Select
              id="template-scope"
              value={draft.scope}
              aria-describedby="template-scope-hint"
              onChange={(event) => update({ scope: event.target.value as TemplateScope })}
            >
              <Select.Option value={TemplateScope.TENANT}>{t('customSurveys.create.scopeCompany')}</Select.Option>
              <Select.Option value={TemplateScope.TEAM}>{t('customSurveys.create.scopeTeam')}</Select.Option>
            </Select>
          </Field>
          {draft.scope === TemplateScope.TEAM && (
            <Field className={classes.selectField}>
              <Label htmlFor="template-team">{t('customSurveys.create.team')}</Label>
              <Select
                id="template-team"
                value={draft.teamId ?? ''}
                aria-invalid={!!teamError}
                aria-describedby={teamError ? 'template-team-error' : undefined}
                onChange={(event) => update({ teamId: event.target.value ? Number(event.target.value) : undefined })}
              >
                <Select.Option value="">{t('customSurveys.create.chooseTeam')}</Select.Option>
                {teams.map((team) => (
                  <Select.Option key={team.id} value={team.id}>
                    {team.name}
                  </Select.Option>
                ))}
              </Select>
              {teamError && <ValidationMessage id="template-team-error">{t(teamError.message)}</ValidationMessage>}
            </Field>
          )}
        </div>
        )}
      </section>

      <section aria-labelledby="template-questions" className={classes.questionsSection}>
        <div className={classes.sectionIntro}>
          <Heading level={2} data-size="sm" id="template-questions">
            {t('customSurveys.create.questions')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {mode === 'create' && count === 1
              ? t('customSurveys.create.questionsFirst')
              : t('customSurveys.create.questionsMany', { count })}
          </Paragraph>
        </div>
        <p role="status" className="ds-sr-only">
          {announcement}
        </p>
        {count > 0 && (
          <ol className={classes.questions} aria-labelledby="template-questions">
            {draft.questions.map((question, index) => (
              <QuestionEditor
                key={question.key}
                question={question}
                number={index + 1}
                total={count}
                open={openKey === question.key}
                errors={errors.filter((error) => error.questionKey === question.key)}
                categories={categories}
                onAddCategory={addCategory}
                onChange={(next) => setQuestions((questions) => questions.map((item) => (item.key === next.key ? next : item)))}
                onToggle={() => setOpenKey((current) => (current === question.key ? null : question.key))}
                onMove={(delta) => moveQuestion(index, delta)}
                onCopy={() => {
                  const copy = copyQuestion(question);
                  setQuestions((questions) => [...questions.slice(0, index + 1), copy, ...questions.slice(index + 1)]);
                  setOpenKey(copy.key);
                }}
                onDelete={() => {
                  setQuestions((questions) => questions.filter((item) => item.key !== question.key));
                  setAnnouncement(t('customSurveys.create.deleted', { number: index + 1 }));
                }}
              />
            ))}
          </ol>
        )}
        <Button id="add-question" variant="secondary" className={classes.addQuestion} onClick={addQuestion}>
          <PlusIcon aria-hidden />
          {t('customSurveys.create.addQuestion')}
        </Button>
      </section>

      <div className={classes.footer}>
        <Button type="submit" loading={isPending}>
          {submitLabel}
        </Button>
        <Button asChild variant="secondary">
          <Link to={cancelTo}>{t('customSurveys.create.cancel')}</Link>
        </Button>
      </div>
    </form>
  );
};
